import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { buttonPress, springConfig } from '../utils/animations';

interface ButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  rounded?: 'full' | 'lg' | 'md';
  isActive?: boolean;
  disabled?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  className = "",
  variant = 'primary',
  size = 'md',
  icon,
  iconPosition = 'left',
  rounded = 'lg',
  isActive = false,
  disabled = false
}) => {
  const prefersReducedMotion = useReducedMotion();

  // Base styles
  const baseStyles = 'inline-flex items-center justify-center font-medium transition-colors duration-200';

  // Size styles
  const sizeStyles = {
    sm: 'px-3 py-1 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg'
  };

  // Rounded styles
  const roundedStyles = {
    md: 'rounded-md',
    lg: 'rounded-lg',
    full: 'rounded-full'
  };

  // Variant styles
  const variantStyles = {
    primary: `bg-blue-500 dark:bg-catppuccin-blue text-white hover:bg-blue-600 dark:hover:bg-catppuccin-lavender ${isActive ? 'bg-blue-700 dark:bg-catppuccin-sapphire' : ''}`,
    secondary: `bg-gray-100 dark:bg-catppuccin-surface1 text-gray-700 dark:text-catppuccin-subtext0 hover:bg-gray-200 dark:hover:bg-catppuccin-surface2 ${isActive ? 'bg-gray-300 dark:bg-catppuccin-overlay0' : ''}`,
    outline: `bg-transparent border border-blue-500 dark:border-catppuccin-blue text-blue-500 dark:text-catppuccin-blue hover:bg-blue-50 dark:hover:bg-catppuccin-surface1 ${isActive ? 'bg-blue-100 dark:bg-catppuccin-surface2' : ''}`,
    ghost: `bg-transparent text-blue-500 dark:text-catppuccin-lavender hover:bg-blue-50 dark:hover:bg-catppuccin-surface1 ${isActive ? 'bg-blue-100 dark:bg-catppuccin-surface2' : ''}`
  };

  // Disabled styles
  const disabledStyles = disabled
    ? 'opacity-50 cursor-not-allowed'
    : 'cursor-pointer';

  // Icon spacing
  const iconSpacing = icon ? (iconPosition === 'left' ? 'space-x-2' : 'space-x-2 flex-row-reverse') : '';

  // Animation variants for the icon
  const iconVariants = {
    rest: { rotate: 0 },
    hover: {
      rotate: [0, -10, 10, 0],
      transition: { duration: 0.4 }
    },
    tap: { scale: 0.9 }
  };

  // If reduced motion is preferred, render without animations
  if (prefersReducedMotion) {
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={`
          ${baseStyles}
          ${sizeStyles[size]}
          ${roundedStyles[rounded]}
          ${variantStyles[variant]}
          ${disabledStyles}
          ${iconSpacing}
          ${className}
        `}
      >
        {icon && <span>{icon}</span>}
        <span>{children}</span>
      </button>
    );
  }

  return (
    <motion.button
      onClick={onClick}
      disabled={disabled}
      className={`
        ${baseStyles}
        ${sizeStyles[size]}
        ${roundedStyles[rounded]}
        ${variantStyles[variant]}
        ${disabledStyles}
        ${iconSpacing}
        ${className}
      `}
      variants={buttonPress}
      initial="rest"
      whileHover={disabled ? undefined : "hover"}
      whileTap={disabled ? undefined : "tap"}
      transition={springConfig.snappy}
    >
      {icon && (
        <motion.span
          variants={iconVariants}
          className="flex items-center"
        >
          {icon}
        </motion.span>
      )}
      <span>{children}</span>
    </motion.button>
  );
};

export default Button;
